import { queryOptions } from '@tanstack/react-query';
import { ToolContextDataService } from '@inference.engine/api-client/web';
import { inferenceHttp } from '@/lib/api/inference-client';
import type { TemplateItem } from './templates.types';

/* eslint-disable i18next/no-literal-string -- wire identifiers and TanStack cache key segments */
export const TEMPLATE_TOOL_ID = 'get_template_v2_tool';
export const CREATOR_AGENT_ID = 'templates-creator-agent';

export const TEMPLATES_QUERY_KEYS = {
  all: ['templatesConfig'] as const,
  list: (organizationId: string, workspaceId: string | null) =>
    ['templatesConfig', 'templates', organizationId, workspaceId] as const,
  /**
   * Prefix of the chat composer's template dropdown key
   * (`['templates', 'templates', organizationId, workspaceId]`, see
   * `features/chats/ui/ChatOptions/TemplatesAgent`). Every write invalidates it so the dropdown
   * cannot go stale after a create / rename / delete performed from these screens.
   */
  chatConsumers: ['templates'] as const,
};
/* eslint-enable i18next/no-literal-string */

export interface TemplatesScope {
  organizationId: string;
  workspaceId: string | null;
}

/**
 * Pick the templates array out of the scoped configs.
 *
 * A workspace context reads ONLY its own config — it deliberately does not fall back to the org
 * config, because a workspace with no config of its own has no templates rather than inheriting
 * the org's. Preserved verbatim from the pre-redesign hook; changing it would silently alter which
 * templates a workspace sees.
 */
function selectTemplates(
  scopes: { orgConfig?: unknown; workspaceConfigs?: unknown } | null | undefined,
  workspaceId: string | null,
): TemplateItem[] {
  if (!scopes) return [];
  const configs = scopes as {
    orgConfig: { data?: unknown } | null;
    workspaceConfigs: Array<{ workspaceId?: string; data?: unknown }>;
  };
  const config = workspaceId
    ? configs.workspaceConfigs.find((c) => c.workspaceId === workspaceId)
    : configs.orgConfig;
  const data = config?.data as { templates?: TemplateItem[] } | undefined;
  return data?.templates ?? [];
}

async function fetchTemplates({ organizationId, workspaceId }: TemplatesScope) {
  const { data } = await ToolContextDataService.findAllByToolId({
    client: inferenceHttp,
    path: { toolId: TEMPLATE_TOOL_ID },
    query: { organizationId, agentId: CREATOR_AGENT_ID },
  });
  return selectTemplates(data, workspaceId);
}

export function templatesQueryOptions({ organizationId, workspaceId }: TemplatesScope) {
  return queryOptions({
    queryKey: TEMPLATES_QUERY_KEYS.list(organizationId, workspaceId),
    queryFn: () => fetchTemplates({ organizationId, workspaceId }),
    enabled: !!organizationId,
  });
}

/**
 * Read the templates array straight from the API, deliberately bypassing the query cache.
 *
 * Every write is read-modify-write over the WHOLE array (the backend stores all templates in one
 * `data` blob), so a write must start from the freshest server state or it silently reverts a
 * sibling template someone else just saved. Going around the cache also leaves an in-flight
 * optimistic update in place — `fetchQuery` would overwrite it with pre-mutation server data and
 * flash the old row back before the write lands.
 */
export const fetchLatestTemplates = fetchTemplates;

export async function upsertTemplates(
  scope: TemplatesScope,
  templates: TemplateItem[],
): Promise<void> {
  await ToolContextDataService.batchUpsert({
    client: inferenceHttp,
    body: {
      items: [
        {
          toolId: TEMPLATE_TOOL_ID,
          organizationId: scope.organizationId,
          workspaceId: scope.workspaceId ?? undefined,
          agentId: CREATOR_AGENT_ID,
          data: { templates },
        },
      ],
    },
  });
}
